import React, { useState, useEffect, Suspense, lazy } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchPermissions } from "./features/permission";
import { getMyThingsDefaultPath } from "./utils/permissions";
import CheckLoginStatus from "./auth/check-login-status";
import {
  BrowserRouter as Router,
  Route,
  Switch,
  Redirect,
} from "react-router-dom";

// import useMaintenanceStatus from "./Hooks/useMaintenanceStatus";
import ClipLoader from "react-spinners/ClipLoader";
import axios from "axios";
import config, { whenConfigReady } from "./config";
import { useLogout } from "./Hooks/useLogout";
import logout from "./utils/logout";
// Static import: rendered outside <Suspense> when maintenance mode is on
import Maintainence from "./maintainence";

const CustomerIntentReport = lazy(() => import("./reports/customer-intent"));

const Login = lazy(() => import("./auth/login"));
const CallList = lazy(() => import("./call-list"));
// Home is declared below with eager chunk prefetch (default route).
const CallInsight = lazy(() => import("./call-insight"));
const TranscriptionAudit = lazy(
  () => import("./call-insight/transcription_audit"),
);
const Leads = lazy(() => import("./opportunities"));
const ServiceRequests = lazy(() => import("./service-requests"));
const ArchiveCall = lazy(() => import("./archive-call"));
const DeepAnalysis = lazy(() => import("./deep-analysis"));
const Contact = lazy(() => import("./contact"));
const LeadDetails = lazy(() => import("./opportunity-details"));
const PromiseDetails = lazy(() => import("./promise-details"));
const SRDetails = lazy(() => import("./service-request-details"));
const ManageUser = lazy(() => import("./user-management/manage-user"));
const ManageRole = lazy(() => import("./user-management/manage-role"));
const AppFeature = lazy(() => import("./user-management/app-feature"));
const ProductIntentReport = lazy(() => import("./reports/product-intent"));
const CXScoreTrendReport = lazy(() => import("./reports/cx-score-trend"));
const ManagerReviewReport = lazy(() => import("./reports/mamager-review"));
const OpportunityReport = lazy(() => import("./reports/opportunity"));
const ServiceRequestReport = lazy(() => import("./reports/service-request"));
const CollectionReport = lazy(() => import("./reports/collection"));
const Performance = lazy(() => import("./reports/performance"));
const Task = lazy(() => import("./My things/task"));
const MyThing = lazy(() => import("./My things/my-thing"));
const MyThingsTranscriptionAudit = lazy(() => import("./My things/transcription-audit"));
const MyThingsCollectionStrategy = lazy(
  () => import("./My things/collection-strategy"),
);
const ContactDetail = lazy(() => import("./contact-detail"));
const LiveCall = lazy(() => import("./live-call"));
const Account = lazy(() => import("./account"));
const QualityHome = lazy(() => import("./quality/quality-home"));
const DashboardHome = lazy(() => import("./reports/dashboard/home"));
const ComplianceReport = lazy(() => import("./reports/compliance"));
const Equity = lazy(() => import("./reports/equity"));
const Mutual = lazy(() => import("./reports/mutual"));
const Forex = lazy(() => import("./reports/forex"));
const Criticalbusiness = lazy(() => import("./reports/audit/criticalbusiness"));
const Criticalcustomer = lazy(() => import("./reports/audit/criticalcustomer"));
const Criticalregulatory = lazy(
  () => import("./reports/audit/criticalregulatory"),
);
const Outputdatabase = lazy(() => import("./reports/audit/outputdatabase"));
const Noncritical = lazy(() => import("./reports/audit/noncritical"));
const PageNotFound = lazy(() => import("./pagenotfound"));
const ComplaintsInsight = lazy(
  () => import("./complaintsManagement/complaintsInsight"),
);
const ComplaintsDeepAnalysis = lazy(
  () => import("./complaintsManagement/complaintDeepAnalysis"),
);
const TicketList = lazy(() => import("./complaintsManagement/ticketlist"));
const ComplaintsServiceRequest = lazy(
  () => import("./complaintsManagement/complaintsServiceRequest"),
);
const ComplaintsArchived = lazy(
  () => import("./complaintsManagement/complaintsArchived"),
);
const Report = lazy(() => import("./reports/mayaBank/report"));
const Promisetopay = lazy(() => import("./promisetopay/promisetopay"));
const Promispay = lazy(() => import("./reports/promistopay"));
const Agentscorecard = lazy(() => import("./reports/agent-scorecard"));
const AgentInsight = lazy(() => import("./reports/agent-insight"));
const Hdfcdreport = lazy(() => import("../src/hdfcdigitals/report"));
const Detailedreport = lazy(() => import("./reports/mayaBank/detailed-report"));
const Predue = lazy(() => import("./reports/mayaBank/predue"));
const Latedue = lazy(() => import("./reports/mayaBank/latedue"));
const Postduegreater = lazy(() => import("./reports/mayaBank/postduegreater"));
const Postdueless = lazy(() => import("./reports/mayaBank/postdueless"));
const Deviance = lazy(() => import("./reports/mayaBank/deviance"));
const Editreport = lazy(() => import("./reports/mayaBank/edit-report"));
const Revisedreport = lazy(() => import("./reports/mayaBank/revised-report"));
const Myreport = lazy(() => import("./reports/my-report"));
const Myreportdetail = lazy(() => import("./reports/my-report-detail"));
const Savedreportdetail = lazy(() => import("./reports/saved-report-details"));
const PostDueGreaterDetail = lazy(
  () => import("./reports/mayaBank/postduegreater-detailed"),
);
const PostDueLessDetail = lazy(
  () => import("./reports/mayaBank/postdueless-details"),
);
const LateDueDetail = lazy(() => import("./reports/mayaBank/latedue-detail"));
const Accesslog = lazy(() => import("./reports/mayaBank/access-log"));
const EconomicNewsCards = lazy(() => import("./Economicnewscards"));
const Recovery = lazy(() => import("./reports/recovery"));
const CollectionStrategy = lazy(
  () => import("./reports/Collection Strategy/collectionStrategy"),
);
const Detail = lazy(() => import("./reports/Collection Strategy/detail"));
const TranscriptionAuditReport = lazy(
  () => import("./reports/Transcription Audit/transcription-audit"),
);
const LobHome = lazy(() => import("./hierarchy/home"));
const ProductIntentReportAll = lazy(
  () => import("./reports/product-intent-all"),
);
const CXScoreTrendReportAll = lazy(
  () => import("./reports/cx-score-trend-all"),
);
const CustomerIntentReportAll = lazy(
  () => import("./reports/customer-intent-all"),
);
const CallTranscriptionAudit = lazy(
  () => import("./call-insight/call-transcription-audit"),
);

// Prefetch Home only when needed (logged-in default route) so login page stays light.
// Same lazy() contract as before: lazy(() => import("./home")).
let homeChunkPromise = null;
const loadHomeChunk = () => {
  if (!homeChunkPromise) {
    homeChunkPromise = import("./home");
  }
  return homeChunkPromise;
};
const Home = lazy(() => loadHomeChunk());

function App() {
  /**
   * Application Initialization Point
   *
   * Navigate to different Components based on router link
   */
  const { loginStatus } = CheckLoginStatus();
  const [isMaintenance, setIsMaintenance] = useState(false);

  useLogout(logout, loginStatus);

  const dispatch = useDispatch();
  const permissionDetail = useSelector(
    (state) => state.permission?.permissionDetail || [],
  );

  // Fetch permission details on app boot and save to Redux store.
  // Wait for config so testing env does not hit the production fallback host.
  useEffect(() => {
    if (!loginStatus) return;
    let cancelled = false;
    whenConfigReady().then(() => {
      if (!cancelled) {
        dispatch(fetchPermissions());
      }
    });
    return () => {
      cancelled = true;
    };
  }, [loginStatus, dispatch]);

  // Prefetch Home chunk after mount when logged in (no side effects during render).
  useEffect(() => {
    if (loginStatus) {
      loadHomeChunk().catch(() => {
        // Chunk load errors are handled by Suspense/route when user opens Home.
      });
    }
  }, [loginStatus]);

  // Maintenance check: still uses the same endpoint and same Maintainence screen.
  // Difference vs old code: we do NOT block the entire app behind a full-page spinner
  // while waiting. If maintenance is active, we still switch to <Maintainence />.
  // If the check fails/times out, we fail open (app usable) — same as the old catch path.
  // whenConfigReady() ensures API_BASE_URL comes from config.json (testing vs prod).
  useEffect(() => {
    let cancelled = false;
    const source = axios.CancelToken.source();

    const checkMaintenance = async () => {
      try {
        await whenConfigReady();
        if (cancelled) return;
        const response = await axios.get(
          `${config.API_BASE_URL}/api/access_control/maintenance/`,
          {
            timeout: 2500,
            cancelToken: source.token,
          },
        );
        // Preserve previous semantics: is_active truthy => maintenance page.
        if (!cancelled) {
          setIsMaintenance(!!response?.data?.is_active);
        }
      } catch (error) {
        if (axios.isCancel(error) || cancelled) return;
        // Fail open (identical to previous catch that set isMaintenance false).
        console.error("Failed to fetch maintenance status:", error);
        if (!cancelled) {
          setIsMaintenance(false);
        }
      }
    };
    checkMaintenance();

    return () => {
      cancelled = true;
      source.cancel("App unmounted");
    };
  }, []);

  if (isMaintenance) {
    return <Maintainence />;
  }

  return (
    <Router>
      <div className="App">
        <Suspense
          fallback={
            <div
              style={{
                textAlign: "center",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "100vh",
              }}
            >
              <ClipLoader color="#2056FF" size="50px" />
            </div>
          }
        >
          <Switch>
            <Route exact path="/">
              <Home loginStatus={loginStatus} />
            </Route>
            <Route exact path="/lob-home">
              <LobHome loginStatus={loginStatus} />
            </Route>
            <Route exact path="/login">
              <Login loginStatus={loginStatus} />
            </Route>
            <Route exact path="/call-list">
              <CallList loginStatus={loginStatus} />
            </Route>
            <Route exact path="/ticket-list">
              <TicketList loginStatus={loginStatus} />
            </Route>
            <Route exact path="/call/:callID/call-insight">
              <CallInsight loginStatus={loginStatus} />
            </Route>
            <Route exact path="/call/:callID/transcription-audit">
              <TranscriptionAudit loginStatus={loginStatus} />
            </Route>
            <Route exact path="/ticket/:callID/complaints-insight">
              <ComplaintsInsight loginStatus={loginStatus} />
            </Route>
            <Route exact path="/call/:callID/call-transcription-audit">
              <CallTranscriptionAudit loginStatus={loginStatus} />
            </Route>
            <Route exact path="/call/:callID/deep-analysis">
              <DeepAnalysis loginStatus={loginStatus} />
            </Route>
            <Route exact path="/ticket/:callID/deep-analysis">
              <ComplaintsDeepAnalysis loginStatus={loginStatus} />
            </Route>
            <Route exact path="/call/opportunities">
              <Leads loginStatus={loginStatus} />
            </Route>
            <Route exact path="/call/call-status">
              <Promisetopay loginStatus={loginStatus} />
            </Route>

            <Route exact path="/call/:leadID/call-status-details">
              <PromiseDetails loginStatus={loginStatus} />
            </Route>
            <Route exact path="/call/:leadID/opportunity-details">
              <LeadDetails loginStatus={loginStatus} />
            </Route>
            <Route exact path="/call/service-requests">
              <ServiceRequests loginStatus={loginStatus} />
            </Route>
            <Route exact path="/ticket/service-requests">
              <ComplaintsServiceRequest loginStatus={loginStatus} />
            </Route>
            <Route exact path="/call/:srID/sr-details">
              <SRDetails loginStatus={loginStatus} />
            </Route>
            <Route exact path="/call/archive-calls">
              <ArchiveCall loginStatus={loginStatus} />
            </Route>
            <Route exact path="/ticket/archive-tickets">
              <ComplaintsArchived loginStatus={loginStatus} />
            </Route>
            <Route exact path="/contact">
              <Contact loginStatus={loginStatus} />
            </Route>
            <Route exact path="/contact/:contactId">
              <ContactDetail loginStatus={loginStatus} />
            </Route>
            <Route exact path="/user-management/manage-user">
              <ManageUser loginStatus={loginStatus} />
            </Route>
            <Route exact path="/user-management/manage-role">
              <ManageRole loginStatus={loginStatus} />
            </Route>
            <Route exact path="/user-management/app-feature">
              <AppFeature loginStatus={loginStatus} />
            </Route>
            <Route exact path="/reports/customer-intent">
              <CustomerIntentReport loginStatus={loginStatus} />
            </Route>
            <Route exact path="/reports/product-intent">
              <ProductIntentReport loginStatus={loginStatus} />
            </Route>
            <Route exact path="/reports/customer-intent-all">
              <CustomerIntentReportAll loginStatus={loginStatus} />
            </Route>
            {/* this route for all the other usecases */}
            <Route exact path="/reports/product-intent-all">
              <ProductIntentReportAll loginStatus={loginStatus} />
            </Route>

            <Route exact path="/reports/forex">
              <Forex />
            </Route>
            <Route exact path="/reports/equity">
              <Equity />
            </Route>
            <Route exact path="/reports/mutual">
              <Mutual />
            </Route>
            <Route exact path="/reports/reports-dashboard">
              <Hdfcdreport />
            </Route>
            <Route exact path="/reports/agent-scorecard/:id">
              <AgentInsight />
            </Route>
            <Route exact path="/reports/agent-scorecard">
              <Agentscorecard />
            </Route>
            <Route exact path="/reports/criticalbusiness">
              <Criticalbusiness />
            </Route>
            <Route exact path="/reports/criticalcustomer">
              <Criticalcustomer />
            </Route>
            <Route exact path="/reports/noncritical">
              <Noncritical />
            </Route>
            <Route exact path="/reports/criticalregulatory">
              <Criticalregulatory />
            </Route>
            <Route exact path="/reports/dashboard">
              <Outputdatabase />
            </Route>
            <Route exact path="/reports">
              <Report />
            </Route>
            {/* collection strategy */}
            <Route exact path="/reports/collection-strategy">
              <CollectionStrategy />
            </Route>
            <Route exact path="/reports/detail/:id/collection-strategy">
              <Detail />
            </Route>

            <Route exact path="/reports/my-reports">
              <Myreport />
            </Route>
            <Route exact path="/access-logs">
              <Accesslog />
            </Route>
            <Route exact path="/reports/my-reports-details">
              <Myreportdetail />
            </Route>
            <Route exact path="/reports/saved-reports-details/:id">
              <Savedreportdetail />
            </Route>
            <Route exact path="/reports/promise-to-pay">
              <Promispay loginStatus={loginStatus} />
            </Route>
            <Route exact path="/detailed-reports/:id">
              <Detailedreport />
            </Route>
            <Route exact path="/postduegreater-detail/:id">
              <PostDueGreaterDetail />
            </Route>
            <Route exact path="/postdueless-detail/:id">
              <PostDueLessDetail />
            </Route>
            <Route exact path="/latedue-detail/:id">
              <LateDueDetail />
            </Route>
            <Route exact path="/reports/predue">
              <Predue />
            </Route>
            <Route exact path="/reports/latedue">
              <Latedue />
            </Route>
            <Route exact path="/reports/postgreater">
              <Postduegreater />
            </Route>
            <Route exact path="/reports/postless">
              <Postdueless />
            </Route>
            <Route exact path="/reports/deviance-analysis">
              <Deviance />
            </Route>
            <Route exact path="/edit-report/:id">
              <Editreport />
            </Route>
            <Route exact path="/report/revised-report/:id">
              <Revisedreport />
            </Route>
            <Route exact path="/reports/cx-score-trend">
              <CXScoreTrendReport loginStatus={loginStatus} />
            </Route>
            {/* cx score for all usecase */}
            <Route exact path="/reports/cx-score-trend-all">
              <CXScoreTrendReportAll loginStatus={loginStatus} />
            </Route>

            <Route exact path="/reports/manager-review">
              <ManagerReviewReport loginStatus={loginStatus} />
            </Route>
            <Route exact path="/reports/opportunity">
              <OpportunityReport loginStatus={loginStatus} />
            </Route>
            <Route exact path="/reports/ptp">
              <Recovery loginStatus={loginStatus} />
            </Route>
            <Route exact path="/reports/service-request">
              <ServiceRequestReport loginStatus={loginStatus} />
            </Route>
            <Route exact path="/reports/collection">
              <CollectionReport loginStatus={loginStatus} />
            </Route>
            <Route exact path="/reports/performance">
              <Performance loginStatus={loginStatus} />
            </Route>
            <Route exact path="/reports/compliance">
              <ComplianceReport loginStatus={loginStatus} />
            </Route>
            <Route exact path="/reports/transcription-report">
              <TranscriptionAuditReport loginStatus={loginStatus} />
            </Route>

            <Route
              exact
              path="/my-things"
              render={() => (
                <Redirect to={getMyThingsDefaultPath(permissionDetail)} />
              )}
            />
            <Route exact path="/my-things/task">
              <Task loginStatus={loginStatus} />
            </Route>
            <Route exact path="/my-things/my-thing">
              <MyThing loginStatus={loginStatus} />
            </Route>
            <Route exact path="/my-things/transcriptionaudit">
              <MyThingsTranscriptionAudit loginStatus={loginStatus} />
            </Route>
            <Route exact path="/my-things/collection-strategy">
              <MyThingsCollectionStrategy loginStatus={loginStatus} />
            </Route>
            <Route exact path="/live-call">
              <LiveCall />
            </Route>
            <Route exact path="/account">
              <Account />
            </Route>
            <Route exact path="/quality-audit">
              <QualityHome />
            </Route>
            <Route exact path="/dashboard">
              <DashboardHome />
            </Route>
            <Route exact path="/media-advisory">
              <EconomicNewsCards />
            </Route>
            <Route path="*" component={PageNotFound} />
          </Switch>
        </Suspense>
      </div>
    </Router>
  );
}

export default App;
